import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useProject } from '../contexts/ProjectContext';
import { useAuth } from '../contexts/AuthContext';
import { BACKEND_URL } from '../utils/api';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Slider } from '../components/ui/slider';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Music, 
  Upload, 
  Film, 
  Clock, 
  Sparkles, 
  Check, 
  ArrowRight,
  ArrowLeft,
  Loader2,
  Play,
  Pause,
  Trash2,
  Plus,
  Settings,
  Eye,
  Download,
  ChevronDown,
  Palette,
  Type,
  Image,
  Zap,
  XCircle,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

const STEPS = [
  { id: 1, name: 'Campaign', icon: Music },
  { id: 2, name: 'Clips', icon: Film },
  { id: 3, name: 'Timeline', icon: Clock },
  { id: 4, name: 'Draft', icon: Eye },
  { id: 5, name: 'Revise', icon: Settings },
  { id: 6, name: 'Export', icon: Download },
];

const SCENE_TYPES = ['performance', 'studio', 'couple', 'b-roll', 'car', 'city', 'abstract'];
const ENERGY_LEVELS = ['low', 'medium', 'high'];
const LIGHTING_TYPES = ['day', 'night', 'neon', 'warm', 'cold'];
const SETTINGS = ['indoors', 'outdoors'];

export default function ProjectEditorPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const isNew = projectId === 'new';
  const { api } = useAuth();
  
  const { 
    currentProject, 
    clips, 
    timeline,
    renders,
    loading,
    loadProject,
    createProject,
    updateProject,
    uploadAudio,
    uploadVideo,
    uploadLogo,
    addClip,
    updateClip,
    deleteClip,
    createTimeline,
    updateTimeline,
    analyzeLyrics,
    createRender,
    fetchRender
  } = useProject();

  const [currentStep, setCurrentStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisJobId, setAnalysisJobId] = useState(null);
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [rendering, setRendering] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    artist_name: '',
    audio_url: '',
    lyrics: '',
    release_links: {},
    brand_kit: {
      logo_url: null,
      colors: ['#2DD4BF', '#FBBF24'],
      fonts: ['Outfit', 'Manrope'],
      watermark_position: 'bottom_right'
    }
  });

  const [audioDuration, setAudioDuration] = useState(0);
  const [selectedClips, setSelectedClips] = useState([]);
  const [sections, setSections] = useState([]);
  const [renderSettings, setRenderSettings] = useState({
    orientation: '9:16',
    quality: '1080p',
    narrative_mode: 'balanced',
    cut_style: 'smooth',
    pace_level: 'medium',
    transition_pack: 'clean_smooth',
    transition_intensity: 'medium',
    motion_pack: 'minimal_kinetic',
    animation_intensity: 'medium',
    text_frequency: 'normal',
    caption_mode: 'lyrics',
    caption_style: 'clean',
    caption_placement: 'bottom_safe',
    color_filter: 'none',
    grain_level: 'off',
    watermark: true,
    watermark_position: 'bottom_right',
    title_card: true,
    cta_outro: true,
    cta_text: 'Out now'
  });
  const [cutdowns, setCutdowns] = useState([
    { duration: 30, platform: 'instagram_reel' },
    { duration: 60, platform: 'facebook_reel' }
  ]);
  const [currentRender, setCurrentRender] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  
  // Clip upload and filter state
  const [isUploadingClips, setIsUploadingClips] = useState(false);
  const [clipUploadProgress, setClipUploadProgress] = useState(0);
  const [uploadingClipCount, setUploadingClipCount] = useState(0);
  const [clipFilter, setClipFilter] = useState('all');
  const [clipSort, setClipSort] = useState('newest');
  const [bulkSelectMode, setBulkSelectMode] = useState(false);
  const [selectedClipIds, setSelectedClipIds] = useState([]);

  // Load project data
  useEffect(() => {
    if (!isNew && projectId) {
      loadProject(projectId).then(project => {
        if (project) {
          setFormData({
            title: project.title,
            artist_name: project.artist_name,
            audio_url: project.audio_url || '',
            lyrics: project.lyrics || '',
            release_links: project.release_links || {},
            brand_kit: project.brand_kit || formData.brand_kit
          });
          // Detect audio duration from uploaded file
          if (project.audio_url) {
            const audio = new Audio();
            audio.preload = 'metadata';
            audio.onloadedmetadata = () => {
              if (audio.duration && isFinite(audio.duration)) {
                setAudioDuration(audio.duration);
              }
            };
            audio.src = `${BACKEND_URL}${project.audio_url}`;
          }
        }
      });
    }
  }, [projectId, isNew, loadProject]);

  // Restore latest render from loaded renders
  useEffect(() => {
    if (renders.length > 0 && !currentRender) {
      const latest = [...renders].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
      setCurrentRender(latest);
      // If there's a render in progress, start polling
      if (latest.status === 'processing' || latest.status === 'queued') {
        setRendering(true);
        const pollRender = setInterval(async () => {
          try {
            const updated = await fetchRender(latest.id);
            setCurrentRender(updated);
            if (updated.status === 'completed' || updated.status === 'failed') {
              clearInterval(pollRender);
              setRendering(false);
              if (updated.status === 'completed') {
                toast.success('Render completed!');
              } else {
                toast.error('Render failed: ' + (updated.error_message || 'Unknown error'));
              }
            }
          } catch { /* ignore polling errors */ }
        }, 2000);
        return () => clearInterval(pollRender);
      }
    }
  }, [renders]);

  useEffect(() => {
    if (timeline?.sections) {
      setSections(timeline.sections);
    }
  }, [timeline]);

  // Handle audio upload with progress and client-side duration detection
  const handleAudioUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    setUploadProgress(0);
    setSaving(true);
    
    try {
      // Upload with progress tracking
      const result = await uploadAudio(file, (progress) => {
        setUploadProgress(progress);
      });
      
      setFormData(prev => ({ ...prev, audio_url: result.file_url }));
      
      // Use server duration if available, otherwise detect client-side
      let finalDuration = result.duration || 0;
      
      if (finalDuration <= 0) {
        // Client-side duration detection as fallback
        finalDuration = await new Promise((resolve) => {
          const audio = new Audio();
          audio.preload = 'metadata';
          audio.onloadedmetadata = () => {
            resolve(audio.duration || 0);
          };
          audio.onerror = () => resolve(0);
          audio.src = `${BACKEND_URL}${result.file_url}`;
          // Timeout fallback
          setTimeout(() => resolve(0), 5000);
        });
      }
      
      setAudioDuration(finalDuration);
      
      const mins = Math.floor(finalDuration / 60);
      const secs = Math.floor(finalDuration % 60);
      const durationStr = finalDuration > 0 ? `${mins}:${secs.toString().padStart(2, '0')}` : 'processing...';
      
      toast.success(`Audio uploaded (${durationStr})`);
    } catch (err) {
      toast.error('Failed to upload audio');
    } finally {
      setSaving(false);
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  // Handle video upload with progress
  const handleVideoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setIsUploadingClips(true);
    setUploadingClipCount(files.length);
    setClipUploadProgress(0);
    
    let uploadedCount = 0;
    
    try {
      for (const file of files) {
        const result = await uploadVideo(file, (progress) => {
          // Calculate overall progress across all files
          const fileProgress = (uploadedCount / files.length) * 100 + (progress / files.length);
          setClipUploadProgress(Math.round(fileProgress));
        });
        
        // Use server duration, fallback to client-side detection
        let finalDuration = result.duration || 0;
        
        if (finalDuration <= 0) {
          // Client-side duration detection fallback
          finalDuration = await new Promise((resolve) => {
            const video = document.createElement('video');
            video.preload = 'metadata';
            video.onloadedmetadata = () => resolve(video.duration || 0);
            video.onerror = () => resolve(0);
            video.src = `${BACKEND_URL}${result.file_url}`;
            setTimeout(() => resolve(0), 10000);
          });
        }
        
        await addClip({
          project_id: currentProject?.id || projectId,
          file_url: result.file_url,
          filename: result.filename,
          duration: finalDuration,
          orientation: result.orientation,
          thumbnail_url: result.thumbnail_url,
          tags: {
            scene_type: 'b-roll',
            energy: 'medium',
            lighting: 'day',
            setting: 'outdoors',
            must_include: false,
            do_not_use: false,
            prefer_chorus: false,
            prefer_verse: false
          }
        });
        
        uploadedCount++;
        setClipUploadProgress(Math.round((uploadedCount / files.length) * 100));
      }
      toast.success(`${files.length} clip(s) uploaded successfully`);
    } catch (err) {
      toast.error(`Failed to upload clips: ${err.message || 'Unknown error'}`);
    } finally {
      setIsUploadingClips(false);
      setClipUploadProgress(0);
      setUploadingClipCount(0);
    }
  };

  // Filter and sort clips
  const getFilteredSortedClips = () => {
    let filtered = [...clips];
    
    // Filter by category
    if (clipFilter !== 'all') {
      filtered = filtered.filter(c => c.tags?.scene_type === clipFilter);
    }
    
    // Sort
    switch (clipSort) {
      case 'oldest':
        filtered.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
        break;
      case 'duration':
        filtered.sort((a, b) => (b.duration || 0) - (a.duration || 0));
        break;
      case 'name':
        filtered.sort((a, b) => a.filename.localeCompare(b.filename));
        break;
      case 'newest':
      default:
        filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        break;
    }
    
    return filtered;
  };

  // Handle logo upload
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setSaving(true);
    try {
      const result = await uploadLogo(file);
      setFormData(prev => ({
        ...prev,
        brand_kit: { ...prev.brand_kit, logo_url: result.file_url }
      }));
      toast.success('Logo uploaded');
    } catch (err) {
      toast.error('Failed to upload logo');
    } finally {
      setSaving(false);
    }
  };

  // Save campaign
  const saveCampaign = async () => {
    if (!formData.title || !formData.artist_name) {
      toast.error('Please fill in required fields');
      return false;
    }

    setSaving(true);
    try {
      if (isNew) {
        const project = await createProject(formData);
        navigate(`/project/${project.id}`, { replace: true });
      } else {
        await updateProject(projectId, formData);
      }
      toast.success('Campaign saved');
      return true;
    } catch (err) {
      toast.error('Failed to save campaign');
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Analyze lyrics with AI (async job)
  const handleAnalyzeLyrics = async () => {
    if (!formData.lyrics.trim()) {
      toast.error('Please enter lyrics first');
      return;
    }

    setAnalyzing(true);
    setAnalysisProgress(0);

    try {
      const res = await api().post('/ai/analyze-lyrics', {
        lyrics: formData.lyrics,
        audio_duration: audioDuration || 180
      });

      const jobId = res.data.job_id;
      setAnalysisJobId(jobId);

      // Poll for job status
      const pollInterval = setInterval(async () => {
        try {
          const jobRes = await api().get(`/ai/jobs/${jobId}`);
          const job = jobRes.data;
          setAnalysisProgress(job.progress || 0);

          if (job.status === 'done') {
            clearInterval(pollInterval);
            setAnalyzing(false);
            setAnalysisJobId(null);
            if (job.result?.sections) {
              setSections(job.result.sections);
            }
            if (job.warning) {
              toast.info(`Analysis complete (${job.warning})`);
            } else {
              toast.success('Lyrics analyzed! Section suggestions ready.');
            }
          } else if (job.status === 'failed') {
            clearInterval(pollInterval);
            setAnalyzing(false);
            setAnalysisJobId(null);
            toast.error('Analysis failed. You can add sections manually.');
          } else if (job.status === 'cancelled') {
            clearInterval(pollInterval);
            setAnalyzing(false);
            setAnalysisJobId(null);
            toast.info('Analysis cancelled.');
          }
        } catch {
          // Polling error, keep trying
        }
      }, 1500);

      // Hard timeout: 2 minutes
      setTimeout(() => {
        clearInterval(pollInterval);
        setAnalyzing(false);
        setAnalysisJobId(null);
      }, 120000);

    } catch (err) {
      toast.error('Failed to start analysis');
      setAnalyzing(false);
      setAnalysisJobId(null);
    }
  };

  const cancelAnalysis = async () => {
    if (analysisJobId) {
      try {
        await api().delete(`/ai/jobs/${analysisJobId}`);
      } catch {
        // ignore
      }
    }
    setAnalyzing(false);
    setAnalysisJobId(null);
    setAnalysisProgress(0);
    toast.info('Analysis cancelled.');
  };

  // Create/update timeline
  const saveTimeline = async () => {
    if (sections.length === 0) {
      toast.error('Please add at least one section');
      return false;
    }

    setSaving(true);
    try {
      if (timeline) {
        await updateTimeline(projectId, { sections, total_duration: audioDuration });
      } else {
        await createTimeline({ project_id: projectId, sections, total_duration: audioDuration });
      }
      toast.success('Timeline saved');
      return true;
    } catch (err) {
      toast.error('Failed to save timeline');
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Start render
  const startRender = async () => {
    setRendering(true);
    try {
      const render = await createRender({
        project_id: projectId,
        settings: renderSettings,
        cutdowns,
        generate_covers: true
      });
      setCurrentRender(render);
      toast.success('Render started!');
      
      // Poll for render status
      const pollRender = setInterval(async () => {
        const updated = await fetchRender(render.id);
        setCurrentRender(updated);
        if (updated.status === 'completed' || updated.status === 'failed') {
          clearInterval(pollRender);
          setRendering(false);
          if (updated.status === 'completed') {
            toast.success('Render completed!');
          } else {
            toast.error('Render failed: ' + updated.error_message);
          }
        }
      }, 2000);
    } catch (err) {
      toast.error('Failed to start render');
      setRendering(false);
    }
  };

  // Navigate steps
  const goToStep = async (step) => {
    // Save current step before navigating
    if (currentStep === 1 && step > 1) {
      const saved = await saveCampaign();
      if (!saved) return;
    }
    if (currentStep === 3 && step > 3 && sections.length > 0) {
      await saveTimeline();
    }
    setCurrentStep(step);
  };

  const nextStep = () => {
    if (currentStep < STEPS.length) {
      goToStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Add section
  const addSection = () => {
    const newSection = {
      id: crypto.randomUUID(),
      name: `Section ${sections.length + 1}`,
      type: 'verse',
      start_time: sections.length > 0 ? sections[sections.length - 1].end_time : 0,
      end_time: (sections.length > 0 ? sections[sections.length - 1].end_time : 0) + 30,
      clip_ids: [],
      lyrics_text: '',
      hook_phrases: []
    };
    setSections([...sections, newSection]);
  };

  // Update section
  const updateSection = (id, updates) => {
    setSections(sections.map(s => s.id === id ? { ...s, ...updates } : s));
  };

  // Delete section
  const removeSection = (id) => {
    setSections(sections.filter(s => s.id !== id));
  };

  // Format time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading && !isNew) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 text-primary animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 max-w-6xl mx-auto" data-testid="project-editor-page">
        {/* Header with Progress */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-6">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/dashboard')}
              className="text-zinc-400 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <h1 className="font-outfit text-2xl font-bold text-white">
              {isNew ? 'New Project' : formData.title || 'Edit Project'}
            </h1>
          </div>

          {/* Wizard Steps */}
          <div className="flex items-center justify-between px-4">
            {STEPS.map((step, index) => {
              const Icon = step.icon;
              const isActive = currentStep === step.id;
              const isCompleted = currentStep > step.id;
              
              return (
                <React.Fragment key={step.id}>
                  <button
                    onClick={() => goToStep(step.id)}
                    className={`flex flex-col items-center gap-2 transition-all ${
                      isActive || isCompleted ? 'cursor-pointer' : 'cursor-default opacity-50'
                    }`}
                    disabled={step.id > currentStep + 1}
                    data-testid={`step-${step.id}`}
                  >
                    <div className={`wizard-step ${isActive ? 'active' : isCompleted ? 'completed' : 'pending'}`}>
                      {isCompleted ? <Check className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                    </div>
                    <span className={`text-xs font-medium ${isActive ? 'text-primary' : isCompleted ? 'text-white' : 'text-zinc-500'}`}>
                      {step.name}
                    </span>
                  </button>
                  {index < STEPS.length - 1 && (
                    <div className={`flex-1 h-0.5 mx-2 ${currentStep > step.id ? 'bg-primary' : 'bg-zinc-800'}`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Step Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
          >
            {/* Step 1: Campaign Setup */}
            {currentStep === 1 && (
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Music className="w-5 h-5 text-primary" />
                    Campaign Setup
                  </CardTitle>
                  <CardDescription className="text-zinc-400">
                    Enter your song details and upload audio
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label className="text-zinc-300">Song Title *</Label>
                      <Input
                        value={formData.title}
                        onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                        placeholder="Enter song title"
                        className="bg-zinc-800/50 border-zinc-700 text-white"
                        data-testid="song-title-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-300">Artist Name *</Label>
                      <Input
                        value={formData.artist_name}
                        onChange={(e) => setFormData(prev => ({ ...prev, artist_name: e.target.value }))}
                        placeholder="Enter artist name"
                        className="bg-zinc-800/50 border-zinc-700 text-white"
                        data-testid="artist-name-input"
                      />
                    </div>
                  </div>

                  {/* Audio Upload */}
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Audio File</Label>
                    <div className="drop-zone flex flex-col items-center justify-center py-8">
                      {isUploading ? (
                        <>
                          <Loader2 className="w-10 h-10 text-primary mb-3 animate-spin" />
                          <p className="text-zinc-300 mb-2">Uploading audio...</p>
                          <div className="w-48">
                            <Progress value={uploadProgress} className="h-2" />
                          </div>
                          <p className="text-primary text-sm mt-2">{uploadProgress}%</p>
                        </>
                      ) : (
                        <>
                          <Upload className="w-10 h-10 text-zinc-500 mb-3" />
                          <p className="text-zinc-400 mb-2">Upload MP3 or WAV</p>
                          <input
                            type="file"
                            accept=".mp3,.wav,.m4a,.aac"
                            onChange={handleAudioUpload}
                            className="hidden"
                            id="audio-upload"
                            data-testid="audio-upload-input"
                          />
                          <Button asChild variant="outline" className="border-zinc-700 text-zinc-300">
                            <label htmlFor="audio-upload" className="cursor-pointer">
                              Choose File
                            </label>
                          </Button>
                        </>
                      )}
                      {formData.audio_url && !isUploading && (
                        <div className="mt-4 flex items-center gap-2 text-primary">
                          <Check className="w-4 h-4" />
                          <span className="text-sm">
                            Audio uploaded {audioDuration > 0 ? `(${formatTime(audioDuration)})` : ''}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Lyrics */}
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Lyrics (Optional)</Label>
                    <Textarea
                      value={formData.lyrics}
                      onChange={(e) => setFormData(prev => ({ ...prev, lyrics: e.target.value }))}
                      placeholder="Paste your lyrics here..."
                      className="bg-zinc-800/50 border-zinc-700 text-white min-h-[150px]"
                      data-testid="lyrics-textarea"
                    />
                  </div>

                  {/* Brand Kit */}
                  <div className="space-y-4">
                    <Label className="text-zinc-300 flex items-center gap-2">
                      <Palette className="w-4 h-4" />
                      Brand Kit (Optional)
                    </Label>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label className="text-zinc-500 text-sm">Logo</Label>
                        <input
                          type="file"
                          accept=".png,.jpg,.jpeg,.svg,.webp"
                          onChange={handleLogoUpload}
                          className="hidden"
                          id="logo-upload"
                        />
                        <Button asChild variant="outline" className="w-full border-zinc-700 text-zinc-300">
                          <label htmlFor="logo-upload" className="cursor-pointer">
                            {formData.brand_kit?.logo_url ? 'Change Logo' : 'Upload Logo'}
                          </label>
                        </Button>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-zinc-500 text-sm">Primary Color</Label>
                        <Input
                          type="color"
                          value={formData.brand_kit?.colors?.[0] || '#2DD4BF'}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            brand_kit: { 
                              ...prev.brand_kit, 
                              colors: [e.target.value, prev.brand_kit?.colors?.[1] || '#FBBF24'] 
                            }
                          }))}
                          className="h-10 p-1 bg-zinc-800/50 border-zinc-700"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-zinc-500 text-sm">Accent Color</Label>
                        <Input
                          type="color"
                          value={formData.brand_kit?.colors?.[1] || '#FBBF24'}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            brand_kit: { 
                              ...prev.brand_kit, 
                              colors: [prev.brand_kit?.colors?.[0] || '#2DD4BF', e.target.value] 
                            }
                          }))}
                          className="h-10 p-1 bg-zinc-800/50 border-zinc-700"
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Step 2: Clips */}
            {currentStep === 2 && (
              <div className="space-y-6">
                {/* Help Card */}
                <Card className="bg-primary/5 border-primary/20">
                  <CardContent className="py-4">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Sparkles className="w-4 h-4 text-primary" />
                      </div>
                      <div>
                        <h4 className="text-white font-medium text-sm mb-1">How tagging works</h4>
                        <p className="text-zinc-400 text-xs leading-relaxed">
                          <strong>Category</strong> determines when clips appear: <em>performance</em> clips in verses/chorus, <em>b-roll</em> for transitions, <em>abstract</em> for intros/outros.
                          <br/><strong>Energy</strong> matches song intensity: <em>high</em> for drops/chorus, <em>low</em> for verses.
                          <br/><strong>Must include</strong> guarantees the clip appears in the final video.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-zinc-900/50 border-zinc-800">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-white flex items-center gap-2">
                          <Film className="w-5 h-5 text-primary" />
                          Upload & Tag Clips
                        </CardTitle>
                        <CardDescription className="text-zinc-400">
                          Upload video clips and tag them for smart editing
                        </CardDescription>
                      </div>
                      {/* Clip count badge */}
                      {clips.length > 0 && (
                        <Badge className="bg-primary/10 text-primary">
                          {clips.length} clip{clips.length !== 1 ? 's' : ''}
                        </Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {/* Upload Zone */}
                    <div className={`drop-zone mb-6 ${clipUploadProgress > 0 ? 'border-primary bg-primary/5' : ''}`}>
                      <input
                        type="file"
                        accept=".mp4,.mov,.avi,.mkv,.webm"
                        multiple
                        onChange={handleVideoUpload}
                        className="hidden"
                        id="video-upload"
                        data-testid="video-upload-input"
                        disabled={isUploadingClips}
                      />
                      {isUploadingClips ? (
                        <div className="flex flex-col items-center justify-center py-8">
                          <Loader2 className="w-10 h-10 text-primary mb-3 animate-spin" />
                          <p className="text-zinc-300 mb-2">Uploading clips...</p>
                          <div className="w-64">
                            <Progress value={clipUploadProgress} className="h-2" />
                          </div>
                          <p className="text-primary text-sm mt-2">{clipUploadProgress}%</p>
                          <p className="text-zinc-500 text-xs mt-1">Processing {uploadingClipCount} clip(s)</p>
                        </div>
                      ) : (
                        <label htmlFor="video-upload" className="flex flex-col items-center justify-center py-8 cursor-pointer">
                          <Upload className="w-10 h-10 text-zinc-500 mb-3" />
                          <p className="text-zinc-400 mb-2">Drag & drop clips or click to upload</p>
                          <p className="text-zinc-500 text-sm">MP4, MOV, AVI, MKV, WebM (multiple files supported)</p>
                        </label>
                      )}
                    </div>

                    {/* Filters and Bulk Actions */}
                    {clips.length > 0 && (
                      <div className="flex flex-wrap items-center gap-3 mb-4 p-3 bg-zinc-800/30 rounded-lg">
                        {/* Filter by category */}
                        <Select value={clipFilter} onValueChange={setClipFilter}>
                          <SelectTrigger className="h-8 w-36 bg-zinc-800 border-zinc-700 text-xs">
                            <SelectValue placeholder="All categories" />
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-900 border-zinc-800">
                            <SelectItem value="all" className="text-zinc-300">All categories</SelectItem>
                            {SCENE_TYPES.map(type => (
                              <SelectItem key={type} value={type} className="text-zinc-300">{type}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>

                        {/* Sort */}
                        <Select value={clipSort} onValueChange={setClipSort}>
                          <SelectTrigger className="h-8 w-32 bg-zinc-800 border-zinc-700 text-xs">
                            <SelectValue placeholder="Sort by" />
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-900 border-zinc-800">
                            <SelectItem value="newest" className="text-zinc-300">Newest first</SelectItem>
                            <SelectItem value="oldest" className="text-zinc-300">Oldest first</SelectItem>
                            <SelectItem value="duration" className="text-zinc-300">Duration</SelectItem>
                            <SelectItem value="name" className="text-zinc-300">Filename</SelectItem>
                          </SelectContent>
                        </Select>

                        <div className="flex-1" />

                        {/* Bulk select toggle */}
                        <Button
                          variant={bulkSelectMode ? "default" : "outline"}
                          size="sm"
                          onClick={() => {
                            setBulkSelectMode(!bulkSelectMode);
                            setSelectedClipIds([]);
                          }}
                          className={bulkSelectMode ? "bg-primary text-primary-foreground" : "border-zinc-700 text-zinc-400"}
                        >
                          {bulkSelectMode ? `${selectedClipIds.length} selected` : 'Bulk edit'}
                        </Button>

                        {/* Bulk actions */}
                        {bulkSelectMode && selectedClipIds.length > 0 && (
                          <Select
                            value=""
                            onValueChange={(v) => {
                              selectedClipIds.forEach(id => {
                                updateClip(id, { tags: { ...clips.find(c => c.id === id)?.tags, scene_type: v } });
                              });
                              toast.success(`Updated ${selectedClipIds.length} clips`);
                            }}
                          >
                            <SelectTrigger className="h-8 w-36 bg-primary/10 border-primary/30 text-xs text-primary">
                              <SelectValue placeholder="Set category" />
                            </SelectTrigger>
                            <SelectContent className="bg-zinc-900 border-zinc-800">
                              {SCENE_TYPES.map(type => (
                                <SelectItem key={type} value={type} className="text-zinc-300">{type}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    )}

                    {/* Clips Grid */}
                    {clips.length > 0 ? (
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {getFilteredSortedClips().map((clip) => (
                          <div 
                            key={clip.id} 
                            className={`clip-card ${bulkSelectMode ? 'cursor-pointer' : ''} ${selectedClipIds.includes(clip.id) ? 'ring-2 ring-primary' : ''}`}
                            data-testid={`clip-${clip.id}`}
                            onClick={() => {
                              if (bulkSelectMode) {
                                setSelectedClipIds(prev => 
                                  prev.includes(clip.id) 
                                    ? prev.filter(id => id !== clip.id)
                                    : [...prev, clip.id]
                                );
                              }
                            }}
                          >
                            {/* Thumbnail with hover preview */}
                            <div 
                              className="aspect-video bg-zinc-800 relative overflow-hidden group"
                              onMouseEnter={(e) => {
                                const video = e.currentTarget.querySelector('video');
                                if (video) video.play();
                              }}
                              onMouseLeave={(e) => {
                                const video = e.currentTarget.querySelector('video');
                                if (video) { video.pause(); video.currentTime = 0; }
                              }}
                            >
                              {clip.thumbnail_url ? (
                                <>
                                  <img 
                                    src={`${BACKEND_URL}${clip.thumbnail_url}`} 
                                    alt={clip.filename}
                                    className="w-full h-full object-cover group-hover:opacity-0 transition-opacity"
                                  />
                                  <video 
                                    src={`${BACKEND_URL}${clip.file_url}`}
                                    className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity"
                                    muted
                                    loop
                                    playsInline
                                  />
                                </>
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <Film className="w-8 h-8 text-zinc-600" />
                                </div>
                              )}
                              
                              {/* Duration badge */}
                              <div className="absolute bottom-2 right-2 bg-black/80 px-1.5 py-0.5 rounded text-xs text-white font-mono">
                                {clip.duration > 0 ? formatTime(clip.duration) : 'Unknown'}
                              </div>
                              
                              {/* Orientation badge */}
                              <Badge 
                                className={`absolute top-2 left-2 text-[10px] ${
                                  clip.orientation === 'portrait' ? 'bg-purple-500/20 text-purple-400' : 'bg-blue-500/20 text-blue-400'
                                }`}
                              >
                                {clip.orientation === 'portrait' ? '9:16' : '16:9'}
                              </Badge>

                              {/* Bulk select checkbox */}
                              {bulkSelectMode && (
                                <div className={`absolute top-2 right-2 w-5 h-5 rounded border-2 flex items-center justify-center ${
                                  selectedClipIds.includes(clip.id) ? 'bg-primary border-primary' : 'border-white/50 bg-black/30'
                                }`}>
                                  {selectedClipIds.includes(clip.id) && <Check className="w-3 h-3 text-white" />}
                                </div>
                              )}

                              {/* Play icon on hover */}
                              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                                <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur flex items-center justify-center">
                                  <Play className="w-5 h-5 text-white ml-0.5" />
                                </div>
                              </div>
                            </div>
                            
                            {/* Info */}
                            <div className="p-3 space-y-2">
                              <p className="text-xs text-zinc-400 truncate" title={clip.filename}>{clip.filename}</p>
                              
                              {/* Tags - compact */}
                              <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                                <Select
                                  value={clip.tags?.scene_type || 'b-roll'}
                                  onValueChange={(v) => updateClip(clip.id, { 
                                    tags: { ...(clip.tags || {}), scene_type: v } 
                                  }).catch(() => toast.error('Failed to update tag'))}
                                >
                                  <SelectTrigger className="h-7 bg-zinc-800 border-zinc-700 text-[10px] flex-1">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent className="bg-zinc-900 border-zinc-800">
                                    {SCENE_TYPES.map(type => (
                                      <SelectItem key={type} value={type} className="text-zinc-300 text-xs">{type}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>

                                <Select
                                  value={clip.tags?.energy || 'medium'}
                                  onValueChange={(v) => updateClip(clip.id, { 
                                    tags: { ...(clip.tags || {}), energy: v } 
                                  }).catch(() => toast.error('Failed to update tag'))}
                                >
                                  <SelectTrigger className="h-7 w-20 bg-zinc-800 border-zinc-700 text-[10px]">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent className="bg-zinc-900 border-zinc-800">
                                    {ENERGY_LEVELS.map(level => (
                                      <SelectItem key={level} value={level} className="text-zinc-300 text-xs">{level}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>

                              {/* Quick toggles */}
                              <div className="flex items-center justify-between text-[10px]">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const newVal = !clip.tags?.must_include;
                                    updateClip(clip.id, {
                                      tags: { ...(clip.tags || {}), must_include: newVal }
                                    }).catch(() => toast.error('Failed to update clip'));
                                  }}
                                  className={`px-2 py-1 rounded ${clip.tags?.must_include ? 'bg-primary/20 text-primary' : 'bg-zinc-800 text-zinc-500'}`}
                                  data-testid={`must-use-toggle-${clip.id}`}
                                >
                                  {clip.tags?.must_include ? '★ Must use' : 'Must use'}
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    deleteClip(clip.id);
                                  }}
                                  className="px-2 py-1 rounded bg-zinc-800 text-zinc-500 hover:bg-destructive/20 hover:text-destructive"
                                  data-testid={`remove-clip-${clip.id}`}
                                >
                                  Remove
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-12 text-zinc-500">
                        <Film className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>No clips uploaded yet</p>
                        <p className="text-xs mt-1">Upload at least a few clips to continue</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Progress Checklist */}
                <Card className="bg-zinc-900/30 border-zinc-800">
                  <CardContent className="py-4">
                    <h4 className="text-white text-sm font-medium mb-3">Ready to continue?</h4>
                    <div className="space-y-2">
                      <div className={`flex items-center gap-2 text-sm ${clips.length > 0 ? 'text-green-400' : 'text-zinc-500'}`}>
                        {clips.length > 0 ? <Check className="w-4 h-4" /> : <div className="w-4 h-4 rounded-full border border-current" />}
                        <span>Upload clips ({clips.length} uploaded)</span>
                      </div>
                      <div className={`flex items-center gap-2 text-sm ${clips.filter(c => c.duration > 0).length === clips.length && clips.length > 0 ? 'text-green-400' : 'text-zinc-500'}`}>
                        {clips.filter(c => c.duration > 0).length === clips.length && clips.length > 0 ? <Check className="w-4 h-4" /> : <div className="w-4 h-4 rounded-full border border-current" />}
                        <span>Durations detected ({clips.filter(c => c.duration > 0).length}/{clips.length})</span>
                      </div>
                      <div className={`flex items-center gap-2 text-sm ${clips.every(c => c.tags?.scene_type) ? 'text-green-400' : 'text-zinc-500'}`}>
                        {clips.every(c => c.tags?.scene_type) ? <Check className="w-4 h-4" /> : <div className="w-4 h-4 rounded-full border border-current" />}
                        <span>Tags assigned (optional but recommended)</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Step 3: Timeline */}
            {currentStep === 3 && (
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-white flex items-center gap-2">
                        <Clock className="w-5 h-5 text-primary" />
                        Song Timeline
                      </CardTitle>
                      <CardDescription className="text-zinc-400">
                        Define song sections and map lyrics
                      </CardDescription>
                    </div>
                    {formData.lyrics && (
                      <div className="flex items-center gap-2">
                        {analyzing && (
                          <Button
                            onClick={cancelAnalysis}
                            variant="outline"
                            size="sm"
                            className="border-zinc-700 text-zinc-400 hover:text-destructive hover:border-destructive"
                            data-testid="cancel-analysis-btn"
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Cancel
                          </Button>
                        )}
                        <Button
                          onClick={handleAnalyzeLyrics}
                          disabled={analyzing}
                          className="bg-primary/10 text-primary hover:bg-primary/20"
                          data-testid="analyze-lyrics-btn"
                        >
                          {analyzing ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Analyzing {analysisProgress}%
                            </>
                          ) : (
                            <>
                              <Sparkles className="w-4 h-4 mr-2" />
                              AI Analyze Lyrics
                            </>
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Analyzer progress */}
                  {analyzing && (
                    <div className="mb-4 p-3 bg-zinc-800/30 rounded-lg space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-zinc-400">AI analyzing lyrics...</span>
                        <span className="text-primary font-mono">{analysisProgress}%</span>
                      </div>
                      <Progress value={analysisProgress} className="h-1.5" />
                      <p className="text-xs text-zinc-500">You can cancel and add sections manually if this takes too long.</p>
                    </div>
                  )}

                  {/* Sections */}
                  <div className="space-y-4 mb-6">
                    {sections.map((section, index) => (
                      <div 
                        key={section.id}
                        className="timeline-section p-4"
                        data-testid={`section-${section.id}`}
                      >
                        <div className="flex items-start gap-4">
                          <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-4">
                            <div className="space-y-1">
                              <Label className="text-xs text-zinc-500">Name</Label>
                              <Input
                                value={section.name}
                                onChange={(e) => updateSection(section.id, { name: e.target.value })}
                                className="h-9 bg-zinc-800/50 border-zinc-700 text-white text-sm"
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs text-zinc-500">Type</Label>
                              <Select
                                value={section.type}
                                onValueChange={(v) => updateSection(section.id, { type: v })}
                              >
                                <SelectTrigger className="h-9 bg-zinc-800/50 border-zinc-700 text-sm">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-zinc-900 border-zinc-800">
                                  {['intro', 'verse', 'chorus', 'bridge', 'outro'].map(type => (
                                    <SelectItem key={type} value={type} className="text-zinc-300 capitalize">{type}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs text-zinc-500">Start</Label>
                              <Input
                                type="number"
                                value={section.start_time}
                                onChange={(e) => updateSection(section.id, { start_time: parseFloat(e.target.value) })}
                                className="h-9 bg-zinc-800/50 border-zinc-700 text-white text-sm font-mono"
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs text-zinc-500">End</Label>
                              <Input
                                type="number"
                                value={section.end_time}
                                onChange={(e) => updateSection(section.id, { end_time: parseFloat(e.target.value) })}
                                className="h-9 bg-zinc-800/50 border-zinc-700 text-white text-sm font-mono"
                              />
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => removeSection(section.id)}
                            className="text-zinc-500 hover:text-destructive"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                        {section.lyrics_text && (
                          <div className="mt-3 p-3 bg-zinc-800/30 rounded text-sm text-zinc-400">
                            {section.lyrics_text}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  <Button
                    variant="outline"
                    onClick={addSection}
                    className="w-full border-dashed border-zinc-700 text-zinc-400 hover:border-primary hover:text-primary"
                    data-testid="add-section-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Section
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Step 4: Draft Preview */}
            {currentStep === 4 && (
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Eye className="w-5 h-5 text-primary" />
                    Draft Preview
                  </CardTitle>
                  <CardDescription className="text-zinc-400">
                    Generate a quick draft to preview your video before final export
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {/* Video Preview */}
                  <div className="aspect-video bg-zinc-800 rounded-lg overflow-hidden mb-6">
                    {currentRender?.status === 'completed' && currentRender?.output_urls?.full_length ? (
                      <video 
                        controls 
                        className="w-full h-full"
                        src={`${BACKEND_URL}${currentRender.output_urls.full_length}`}
                        data-testid="video-preview"
                      />
                    ) : currentRender?.status === 'processing' || currentRender?.status === 'queued' ? (
                      <div className="w-full h-full flex flex-col items-center justify-center">
                        <Loader2 className="w-12 h-12 text-primary animate-spin mb-4" />
                        <p className="text-zinc-300 font-medium">Rendering draft...</p>
                        <p className="text-zinc-400 text-sm mt-1">{currentRender.progress || 0}%</p>
                        <Progress value={currentRender.progress || 0} className="w-64 mt-4 h-2" />
                        <p className="text-zinc-500 text-xs mt-2">This may take a few minutes</p>
                      </div>
                    ) : currentRender?.status === 'failed' ? (
                      <div className="w-full h-full flex flex-col items-center justify-center p-6">
                        <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
                          <XCircle className="w-8 h-8 text-destructive" />
                        </div>
                        <p className="text-destructive font-medium">Render failed</p>
                        <p className="text-zinc-500 text-sm mt-2 text-center max-w-md">{currentRender.error_message || 'An error occurred during rendering. Try again or adjust your settings.'}</p>
                        <Button 
                          onClick={startRender} 
                          className="mt-4 bg-primary text-primary-foreground"
                          data-testid="retry-render-btn"
                        >
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Retry Render
                        </Button>
                      </div>
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center p-6">
                        <Film className="w-16 h-16 text-zinc-600 mb-4" />
                        <p className="text-zinc-300 font-medium">Ready to generate draft</p>
                        <p className="text-zinc-500 text-sm mt-2 text-center max-w-md">
                          Click below to render a preview of your music video using your clips and audio.
                        </p>
                        <Button 
                          onClick={startRender}
                          disabled={rendering || clips.length === 0 || !formData.audio_url}
                          className="mt-4 bg-primary text-primary-foreground hover:bg-primary/90 glow-primary"
                          data-testid="generate-draft-btn"
                        >
                          {rendering ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Starting render...
                            </>
                          ) : (
                            <>
                              <Zap className="w-4 h-4 mr-2" />
                              Generate Draft Preview
                            </>
                          )}
                        </Button>
                        {clips.length === 0 && (
                          <p className="text-yellow-500/70 text-xs mt-3">Upload clips first (Step 2)</p>
                        )}
                        {!formData.audio_url && (
                          <p className="text-yellow-500/70 text-xs mt-1">Upload audio first (Step 1)</p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Quick Settings */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="space-y-2">
                      <Label className="text-zinc-400 text-sm">Orientation</Label>
                      <Select
                        value={renderSettings.orientation}
                        onValueChange={(v) => setRenderSettings(prev => ({ ...prev, orientation: v }))}
                      >
                        <SelectTrigger className="bg-zinc-800 border-zinc-700">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-zinc-800">
                          <SelectItem value="9:16" className="text-zinc-300">9:16 Vertical</SelectItem>
                          <SelectItem value="16:9" className="text-zinc-300">16:9 Horizontal</SelectItem>
                          <SelectItem value="1:1" className="text-zinc-300">1:1 Square</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-400 text-sm">Cut Style</Label>
                      <Select
                        value={renderSettings.cut_style}
                        onValueChange={(v) => setRenderSettings(prev => ({ ...prev, cut_style: v }))}
                      >
                        <SelectTrigger className="bg-zinc-800 border-zinc-700">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-zinc-800">
                          <SelectItem value="smooth" className="text-zinc-300">Smooth</SelectItem>
                          <SelectItem value="punchy" className="text-zinc-300">Punchy</SelectItem>
                          <SelectItem value="cinematic" className="text-zinc-300">Cinematic</SelectItem>
                          <SelectItem value="hyper" className="text-zinc-300">Hyper</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-400 text-sm">Transitions</Label>
                      <Select
                        value={renderSettings.transition_pack}
                        onValueChange={(v) => setRenderSettings(prev => ({ ...prev, transition_pack: v }))}
                      >
                        <SelectTrigger className="bg-zinc-800 border-zinc-700">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-zinc-800">
                          <SelectItem value="clean_smooth" className="text-zinc-300">Clean Smooth</SelectItem>
                          <SelectItem value="modern_social" className="text-zinc-300">Modern Social</SelectItem>
                          <SelectItem value="cinematic" className="text-zinc-300">Cinematic</SelectItem>
                          <SelectItem value="dance_energy" className="text-zinc-300">Dance Energy</SelectItem>
                          <SelectItem value="minimal" className="text-zinc-300">Minimal</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-400 text-sm">Pace</Label>
                      <Select
                        value={renderSettings.pace_level}
                        onValueChange={(v) => setRenderSettings(prev => ({ ...prev, pace_level: v }))}
                      >
                        <SelectTrigger className="bg-zinc-800 border-zinc-700">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-zinc-800">
                          <SelectItem value="slow" className="text-zinc-300">Slow</SelectItem>
                          <SelectItem value="medium" className="text-zinc-300">Medium</SelectItem>
                          <SelectItem value="fast" className="text-zinc-300">Fast</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Step 5: Revise */}
            {currentStep === 5 && (
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Settings className="w-5 h-5 text-primary" />
                    Render Settings
                  </CardTitle>
                  <CardDescription className="text-zinc-400">
                    Fine-tune your video output settings
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Tabs defaultValue="video" className="w-full">
                    <TabsList className="bg-zinc-800 mb-6">
                      <TabsTrigger value="video" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Video</TabsTrigger>
                      <TabsTrigger value="effects" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Effects</TabsTrigger>
                      <TabsTrigger value="captions" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Captions</TabsTrigger>
                      <TabsTrigger value="brand" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Brand</TabsTrigger>
                    </TabsList>

                    <TabsContent value="video" className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label className="text-zinc-300">Narrative Mode</Label>
                          <Select
                            value={renderSettings.narrative_mode}
                            onValueChange={(v) => setRenderSettings(prev => ({ ...prev, narrative_mode: v }))}
                          >
                            <SelectTrigger className="bg-zinc-800 border-zinc-700">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-zinc-900 border-zinc-800">
                              <SelectItem value="performance" className="text-zinc-300">Performance Focused</SelectItem>
                              <SelectItem value="story" className="text-zinc-300">Story Focused</SelectItem>
                              <SelectItem value="balanced" className="text-zinc-300">Balanced</SelectItem>
                              <SelectItem value="abstract" className="text-zinc-300">Abstract Montage</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-zinc-300">Motion Graphics</Label>
                          <Select
                            value={renderSettings.motion_pack}
                            onValueChange={(v) => setRenderSettings(prev => ({ ...prev, motion_pack: v }))}
                          >
                            <SelectTrigger className="bg-zinc-800 border-zinc-700">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-zinc-900 border-zinc-800">
                              <SelectItem value="minimal_kinetic" className="text-zinc-300">Minimal Kinetic</SelectItem>
                              <SelectItem value="bold_promo" className="text-zinc-300">Bold Promo</SelectItem>
                              <SelectItem value="neon_club" className="text-zinc-300">Neon Club</SelectItem>
                              <SelectItem value="luxury_gloss" className="text-zinc-300">Luxury Gloss</SelectItem>
                              <SelectItem value="gritty_urban" className="text-zinc-300">Gritty Urban</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <Label className="text-zinc-300">Animation Intensity</Label>
                        <div className="flex items-center gap-4">
                          <span className="text-zinc-500 text-sm w-16">Low</span>
                          <Slider
                            value={[renderSettings.animation_intensity === 'low' ? 0 : renderSettings.animation_intensity === 'medium' ? 50 : 100]}
                            onValueChange={([v]) => setRenderSettings(prev => ({ 
                              ...prev, 
                              animation_intensity: v < 33 ? 'low' : v < 66 ? 'medium' : 'high' 
                            }))}
                            max={100}
                            step={1}
                            className="flex-1"
                          />
                          <span className="text-zinc-500 text-sm w-16 text-right">High</span>
                        </div>
                      </div>
                    </TabsContent>

                    <TabsContent value="effects" className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label className="text-zinc-300">Color Filter</Label>
                          <Select
                            value={renderSettings.color_filter}
                            onValueChange={(v) => setRenderSettings(prev => ({ ...prev, color_filter: v }))}
                          >
                            <SelectTrigger className="bg-zinc-800 border-zinc-700">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-zinc-900 border-zinc-800">
                              <SelectItem value="none" className="text-zinc-300">None (Original)</SelectItem>
                              <SelectItem value="warm_film" className="text-zinc-300">Warm Film</SelectItem>
                              <SelectItem value="cool_cinematic" className="text-zinc-300">Cool Cinematic</SelectItem>
                              <SelectItem value="high_contrast" className="text-zinc-300">High Contrast</SelectItem>
                              <SelectItem value="soft_glow" className="text-zinc-300">Soft Glow</SelectItem>
                              <SelectItem value="night_neon" className="text-zinc-300">Night Neon</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-zinc-300">Film Grain</Label>
                          <Select
                            value={renderSettings.grain_level}
                            onValueChange={(v) => setRenderSettings(prev => ({ ...prev, grain_level: v }))}
                          >
                            <SelectTrigger className="bg-zinc-800 border-zinc-700">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-zinc-900 border-zinc-800">
                              <SelectItem value="off" className="text-zinc-300">Off</SelectItem>
                              <SelectItem value="low" className="text-zinc-300">Low</SelectItem>
                              <SelectItem value="medium" className="text-zinc-300">Medium</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <Label className="text-zinc-300">Transition Intensity</Label>
                        <div className="flex items-center gap-4">
                          <span className="text-zinc-500 text-sm w-16">Low</span>
                          <Slider
                            value={[renderSettings.transition_intensity === 'low' ? 0 : renderSettings.transition_intensity === 'medium' ? 50 : 100]}
                            onValueChange={([v]) => setRenderSettings(prev => ({ 
                              ...prev, 
                              transition_intensity: v < 33 ? 'low' : v < 66 ? 'medium' : 'high' 
                            }))}
                            max={100}
                            step={1}
                            className="flex-1"
                          />
                          <span className="text-zinc-500 text-sm w-16 text-right">High</span>
                        </div>
                      </div>
                    </TabsContent>

                    <TabsContent value="captions" className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label className="text-zinc-300">Caption Mode</Label>
                          <Select
                            value={renderSettings.caption_mode}
                            onValueChange={(v) => setRenderSettings(prev => ({ ...prev, caption_mode: v }))}
                          >
                            <SelectTrigger className="bg-zinc-800 border-zinc-700">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-zinc-900 border-zinc-800">
                              <SelectItem value="lyrics" className="text-zinc-300">Lyrics Captions</SelectItem>
                              <SelectItem value="transcription" className="text-zinc-300">Transcription</SelectItem>
                              <SelectItem value="hook_only" className="text-zinc-300">Hook Only</SelectItem>
                              <SelectItem value="none" className="text-zinc-300">No Captions</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-zinc-300">Caption Style</Label>
                          <Select
                            value={renderSettings.caption_style}
                            onValueChange={(v) => setRenderSettings(prev => ({ ...prev, caption_style: v }))}
                          >
                            <SelectTrigger className="bg-zinc-800 border-zinc-700">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-zinc-900 border-zinc-800">
                              <SelectItem value="clean" className="text-zinc-300">Clean Subtitles</SelectItem>
                              <SelectItem value="bold_outlined" className="text-zinc-300">Bold Outlined</SelectItem>
                              <SelectItem value="karaoke" className="text-zinc-300">Karaoke Highlight</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-zinc-300">Caption Placement</Label>
                          <Select
                            value={renderSettings.caption_placement}
                            onValueChange={(v) => setRenderSettings(prev => ({ ...prev, caption_placement: v }))}
                          >
                            <SelectTrigger className="bg-zinc-800 border-zinc-700">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-zinc-900 border-zinc-800">
                              <SelectItem value="bottom_safe" className="text-zinc-300">Bottom Safe</SelectItem>
                              <SelectItem value="mid_safe" className="text-zinc-300">Mid Safe</SelectItem>
                              <SelectItem value="dynamic" className="text-zinc-300">Dynamic</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-zinc-300">Text Frequency</Label>
                          <Select
                            value={renderSettings.text_frequency}
                            onValueChange={(v) => setRenderSettings(prev => ({ ...prev, text_frequency: v }))}
                          >
                            <SelectTrigger className="bg-zinc-800 border-zinc-700">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-zinc-900 border-zinc-800">
                              <SelectItem value="rare" className="text-zinc-300">Rare</SelectItem>
                              <SelectItem value="normal" className="text-zinc-300">Normal</SelectItem>
                              <SelectItem value="frequent" className="text-zinc-300">Frequent</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </TabsContent>

                    <TabsContent value="brand" className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="flex items-center justify-between p-4 bg-zinc-800/50 rounded-lg">
                          <div>
                            <Label className="text-zinc-300">Watermark</Label>
                            <p className="text-xs text-zinc-500 mt-1">Show logo watermark</p>
                          </div>
                          <Switch
                            checked={renderSettings.watermark}
                            onCheckedChange={(v) => setRenderSettings(prev => ({ ...prev, watermark: v }))}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-zinc-300">Watermark Position</Label>
                          <Select
                            value={renderSettings.watermark_position}
                            onValueChange={(v) => setRenderSettings(prev => ({ ...prev, watermark_position: v }))}
                            disabled={!renderSettings.watermark}
                          >
                            <SelectTrigger className="bg-zinc-800 border-zinc-700">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-zinc-900 border-zinc-800">
                              <SelectItem value="bottom_right" className="text-zinc-300">Bottom Right</SelectItem>
                              <SelectItem value="bottom_left" className="text-zinc-300">Bottom Left</SelectItem>
                              <SelectItem value="top_right" className="text-zinc-300">Top Right</SelectItem>
                              <SelectItem value="top_left" className="text-zinc-300">Top Left</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex items-center justify-between p-4 bg-zinc-800/50 rounded-lg">
                          <div>
                            <Label className="text-zinc-300">Title Card</Label>
                            <p className="text-xs text-zinc-500 mt-1">Show title at start</p>
                          </div>
                          <Switch
                            checked={renderSettings.title_card}
                            onCheckedChange={(v) => setRenderSettings(prev => ({ ...prev, title_card: v }))}
                          />
                        </div>
                        <div className="flex items-center justify-between p-4 bg-zinc-800/50 rounded-lg">
                          <div>
                            <Label className="text-zinc-300">CTA Outro</Label>
                            <p className="text-xs text-zinc-500 mt-1">Show call-to-action at end</p>
                          </div>
                          <Switch
                            checked={renderSettings.cta_outro}
                            onCheckedChange={(v) => setRenderSettings(prev => ({ ...prev, cta_outro: v }))}
                          />
                        </div>
                      </div>
                      {renderSettings.cta_outro && (
                        <div className="space-y-2">
                          <Label className="text-zinc-300">CTA Text</Label>
                          <Input
                            value={renderSettings.cta_text}
                            onChange={(e) => setRenderSettings(prev => ({ ...prev, cta_text: e.target.value }))}
                            placeholder="Out now"
                            className="bg-zinc-800 border-zinc-700 text-white"
                          />
                        </div>
                      )}
                    </TabsContent>
                  </Tabs>
                </CardContent>
              </Card>
            )}

            {/* Step 6: Export */}
            {currentStep === 6 && (
              <div className="space-y-6">
                {/* Download Section - ALWAYS FIRST */}
                {currentRender?.status === 'completed' && currentRender?.output_urls && (
                  <Card className="bg-zinc-900/50 border-primary/30">
                    <CardHeader>
                      <CardTitle className="text-white flex items-center gap-2">
                        <Download className="w-5 h-5 text-primary" />
                        Download Your Videos
                      </CardTitle>
                      <CardDescription className="text-zinc-400">
                        Your render is complete. Download your files below.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Full Length Video Preview */}
                      {currentRender.output_urls.full_length && (
                        <div className="space-y-3">
                          <div className="aspect-video bg-zinc-800 rounded-lg overflow-hidden max-w-2xl">
                            <video 
                              controls 
                              className="w-full h-full"
                              src={`${BACKEND_URL}${currentRender.output_urls.full_length}`}
                              data-testid="final-video-preview"
                            />
                          </div>
                        </div>
                      )}

                      {/* Download Buttons */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {Object.entries(currentRender.output_urls).map(([key, url]) => (
                          <a
                            key={key}
                            href={`${BACKEND_URL}${url}`}
                            download
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-3 p-4 border border-zinc-700 rounded-lg text-zinc-200 hover:bg-primary/5 hover:border-primary/40 transition-all group"
                            data-testid={`download-${key}`}
                          >
                            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                              <Download className="w-5 h-5 text-primary" />
                            </div>
                            <div>
                              <p className="text-sm font-medium">{key === 'full_length' ? 'Full Length Video' : key.replace(/_/g, ' ')}</p>
                              <p className="text-xs text-zinc-500">MP4</p>
                            </div>
                          </a>
                        ))}
                      </div>

                      {/* Cover Images */}
                      {currentRender.cover_urls?.length > 0 && (
                        <div className="space-y-2 pt-2">
                          <Label className="text-zinc-400 text-sm">Cover Images</Label>
                          <div className="flex gap-3">
                            {currentRender.cover_urls.map((url, idx) => (
                              <a
                                key={idx}
                                href={`${BACKEND_URL}${url}`}
                                download
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-24 h-24 border border-zinc-700 rounded-lg overflow-hidden hover:border-primary transition-colors"
                                data-testid={`download-cover-${idx}`}
                              >
                                <img 
                                  src={`${BACKEND_URL}${url}`} 
                                  alt={`Cover ${idx + 1}`}
                                  className="w-full h-full object-cover"
                                />
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Render Controls Card */}
                <Card className="bg-zinc-900/50 border-zinc-800">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Zap className="w-5 h-5 text-primary" />
                      {currentRender?.status === 'completed' ? 'Re-render or Create Cutdowns' : 'Render Your Video'}
                    </CardTitle>
                    <CardDescription className="text-zinc-400">
                      {currentRender?.status === 'completed' 
                        ? 'Adjust settings and render again, or select different cutdowns'
                        : 'Select cutdown formats and start your render'}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Render includes full length - always on */}
                    <div className="p-3 bg-zinc-800/30 rounded-lg flex items-center gap-3">
                      <Check className="w-5 h-5 text-primary" />
                      <div>
                        <p className="text-sm text-zinc-200">Full Length Video (MP4)</p>
                        <p className="text-xs text-zinc-500">Always included in every render</p>
                      </div>
                    </div>

                    {/* Cutdowns Selection */}
                    <div className="space-y-3">
                      <Label className="text-zinc-300">Additional Cutdowns (optional)</Label>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                        {[
                          { duration: 15, platform: 'instagram_reel', label: 'IG Reel 15s' },
                          { duration: 30, platform: 'instagram_reel', label: 'IG Reel 30s' },
                          { duration: 30, platform: 'facebook_reel', label: 'FB Reel 30s' },
                          { duration: 15, platform: 'instagram_story', label: 'IG Story 15s' },
                          { duration: 15, platform: 'facebook_story', label: 'FB Story 15s' },
                        ].map((option) => {
                          const isSelected = cutdowns.some(c => c.duration === option.duration && c.platform === option.platform);
                          return (
                            <button
                              key={`${option.platform}-${option.duration}`}
                              onClick={() => {
                                if (isSelected) {
                                  setCutdowns(cutdowns.filter(c => !(c.duration === option.duration && c.platform === option.platform)));
                                } else {
                                  setCutdowns([...cutdowns, option]);
                                }
                              }}
                              className={`p-3 rounded-lg border text-sm transition-all ${
                                isSelected 
                                  ? 'border-primary bg-primary/10 text-primary' 
                                  : 'border-zinc-700 text-zinc-400 hover:border-zinc-600'
                              }`}
                              data-testid={`cutdown-${option.platform}-${option.duration}`}
                            >
                              {option.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Render Status */}
                    {currentRender && currentRender.status !== 'completed' && (
                      <div className="p-4 bg-zinc-800/50 rounded-lg space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-zinc-300">Render Status</span>
                          <Badge className={
                            currentRender.status === 'failed' ? 'bg-red-500/20 text-red-400' :
                            currentRender.status === 'processing' ? 'bg-yellow-500/20 text-yellow-400' :
                            'bg-zinc-500/20 text-zinc-400'
                          }
                          data-testid="render-status-badge"
                          >
                            {currentRender.status}
                          </Badge>
                        </div>
                        {(currentRender.status === 'processing' || currentRender.status === 'queued') && (
                          <>
                            <Progress value={currentRender.progress} className="h-2" />
                            <p className="text-xs text-zinc-500">{currentRender.progress}% complete</p>
                          </>
                        )}
                        {currentRender.status === 'failed' && (
                          <div className="p-3 bg-destructive/10 rounded text-sm text-destructive">
                            {currentRender.error_message || 'Render failed. Try again or adjust your settings.'}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Render Button */}
                    <Button
                      onClick={startRender}
                      disabled={rendering || clips.length === 0 || !formData.audio_url}
                      className="w-full bg-primary text-primary-foreground hover:bg-primary/90 glow-primary h-12 text-lg"
                      data-testid="start-render-btn"
                    >
                      {rendering ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Rendering...
                        </>
                      ) : (
                        <>
                          <Zap className="w-5 h-5 mr-2" />
                          {currentRender?.status === 'completed' ? 'Re-render Video' : 'Start Final Render'}
                        </>
                      )}
                    </Button>

                    {clips.length === 0 && (
                      <p className="text-center text-zinc-500 text-sm">
                        Please upload clips before rendering
                      </p>
                    )}
                    
                    {!formData.audio_url && (
                      <p className="text-center text-zinc-500 text-sm">
                        Please upload audio before rendering
                      </p>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Navigation Buttons */}
        <div className="flex items-center justify-between mt-8">
          <Button
            variant="outline"
            onClick={prevStep}
            disabled={currentStep === 1}
            className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Previous
          </Button>
          
          {currentStep < STEPS.length ? (
            <Button
              onClick={nextStep}
              disabled={saving}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
              data-testid="next-step-btn"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : null}
              Continue
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={() => navigate('/scheduler')}
              className="bg-secondary text-secondary-foreground hover:bg-secondary/90"
            >
              Schedule Posts
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </div>
    </Layout>
  );
}
